import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Check, ChevronLeft, Copy, GitBranch, Loader2, Minus, Play, Plus, Rows3, Trash2, X,
} from "lucide-react";
import type { OpenRouterModel } from "../api/client";
import {
  clearTrayScope, consolidateRahafDraft, getBundleV2, recordRuling, resolveTrayItem, streamExecute,
  type ExecCheckRecord, type ExecResult, type ExecutionSettings, type TrayItem,
} from "../api/workbench";
import { replaceDraftV2 } from "../api/workbench";
import { deleteAgent as apiDeleteAgent, diffDraft, duplicateBundle, revertDraft, saveVersion, updateBundle } from "../api/agentBundles";
import { fmtDate } from "../utils";
import type { CorpusSet, WorkstreamLite } from "../types";
import { AgentBuilder } from "./AgentBuilder";
import { RunResultPanel, type RunPanelData } from "./RunResultPanel";
import {
  agentDeps, conditionSummary, deriveStages, freshAgent, setBundleModel,
  type AgentDefV2, type BundleDetailV2, type BundleMapV2, type WorkbenchMeta,
} from "./types";

/* ===================================================================
   Agent Bundle view — prototype chrome: slim header band, version rail
   on the left, staged canvas (or graph) behind a single-line toolbar.
   Running one email annotates the canvas (fired / skipped + why) and
   opens the run-result drawer on the right. Clicking a card opens the
   Agent Builder; clicking a version inspects it read-only.
=================================================================== */

export interface BundleViewProps {
  bundleId: string;
  meta: WorkbenchMeta;
  models: OpenRouterModel[];
  settings: ExecutionSettings;
  corpuses: CorpusSet[];
  workstreams: WorkstreamLite[];
  onBack: () => void;
  onDeleted: () => void;
}

const AMBER = "var(--amber)";

type RunStatus = "running" | "done" | "skipped" | "failed";
interface RunMark {
  status: RunStatus;
  why?: string;
}

function checkToMark(c: ExecCheckRecord): RunMark {
  if (c.skipped) return { status: "skipped", why: c.reason };
  if (c.parked || !c.ok) return { status: "failed", why: c.out || c.reason };
  return { status: "done" };
}

/** Once an agent succeeds for any matter, that "done" wins over a later
    skip/fail/running from another matter pass. */
function mergeMark(prev: Record<string, RunMark>, id: string, next: RunMark): Record<string, RunMark> {
  if (prev[id]?.status === "done" && next.status !== "done") return prev;
  return { ...prev, [id]: next };
}

function AgentCard(p: {
  id: string;
  def: AgentDefV2;
  deps: string[];
  bundle: BundleMapV2;
  meta: WorkbenchMeta;
  seed: boolean;
  mark: RunMark | null;
  onOpen: () => void;
}): JSX.Element {
  const { def } = p;
  const sigLabel = (id: string): string => p.meta.signals.find((s) => s.id === id)?.label ?? id;
  const summary = conditionSummary(def.cond, sigLabel);
  const hasSem = def.cond.some((c) => c.t === "sem");
  /* Chips show only the explicit agent-output inputs (the prototype's rule);
     spine-implied and signal-derived deps still order the canvas via deps. */
  const depChips = def.inputs.filter((i) => i.startsWith("agent:")).map((i) => i.slice(6));
  const spineDeps = def.inputs.includes("spine") ? p.deps.length - depChips.length : 0;
  const markCls =
    p.mark?.status === "done" ? " fired"
    : p.mark?.status === "running" ? " running"
    : p.mark?.status === "failed" ? " failed"
    : p.mark?.status === "skipped" ? " skip"
    : "";
  const cls = "wcard" + (!p.seed ? " custom" : "") + (!def.enabled ? " off" : "") + markCls;
  return (
    <button className={cls} onClick={p.onOpen}>
      <span className="nm">
        {p.mark?.status === "running" && <Loader2 size={12} className="spinrot" style={{ color: "var(--route)" }} />}
        {def.name}
      </span>
      <span className="meta">
        {def.role !== "agent" && <span className="chip role">{def.role}</span>}
        <span className="chip mono">{def.model.split("/").pop()}</span>
        <span className="chip mono">τ {def.tau.toFixed(2)}</span>
        {depChips.map((d) => (
          <span key={d} className="chip dep" title={`Runs after ${p.bundle[d]?.name ?? d}`}>{d}↗</span>
        ))}
        {spineDeps > 0 && (
          <span className="chip dep" title={`Reads the spine digest — runs after ${spineDeps} comprehension agents`}>spine↗</span>
        )}
        {hasSem && <span className="chip sem">semantic</span>}
      </span>
      <span className="trig">{def.cond.length === 0 ? "Always fires" : `Fires when ${summary}`}</span>
      {p.mark && (p.mark.status === "skipped" || p.mark.status === "failed") && (
        <span className="whyskip">✕ {p.mark.why || (p.mark.status === "failed" ? "output parked" : "triggers did not fire")}</span>
      )}
    </button>
  );
}

/* Live agent checklist shown in the drawer while a run is in flight, before
   the full result panel replaces it. Mirrors the canvas marks in stage order. */
function RunProgress(p: {
  stages: Array<{ title: string; ids: string[] }>;
  map: BundleMapV2;
  marks: Record<string, RunMark>;
  done: number;
}): JSX.Element {
  const running = Object.values(p.marks).filter((m) => m.status === "running").length;
  const dot = (m: RunMark | undefined): JSX.Element => {
    if (!m) return <span style={{ width: 14, height: 14, borderRadius: "50%", border: "1.5px solid var(--bd2)", flex: "none" }} />;
    if (m.status === "running") return <Loader2 size={13} className="spinrot" style={{ color: "var(--route)", flex: "none" }} />;
    if (m.status === "done") return <Check size={13} style={{ color: "var(--acc)", flex: "none" }} />;
    if (m.status === "failed") return <X size={13} style={{ color: "var(--bad)", flex: "none" }} />;
    return <Minus size={13} style={{ color: "var(--tert)", flex: "none" }} />;
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <div className="rsum" style={{ flexShrink: 0 }}>
        <div className="l">
          <span><Loader2 size={13} className="spinrot" style={{ verticalAlign: "-2px" }} /> <b>Running…</b></span>
          <span><b>{p.done}</b> processed</span>
          {running > 0 && <span>{running} in flight</span>}
        </div>
      </div>
      <div className="scroll" style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {p.stages.map((s) => (
          <div key={s.title} style={{ marginBottom: 10 }}>
            <div className="up tert" style={{ fontSize: 10, marginBottom: 4 }}>{s.title}</div>
            {s.ids.map((id) => {
              const m = p.marks[id];
              return (
                <div key={id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", fontSize: 12.5, opacity: m ? 1 : 0.5 }}>
                  {dot(m)}
                  <span style={{ fontWeight: m?.status === "running" ? 600 : 400, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.map[id]?.name ?? id}
                  </span>
                  {m?.status === "skipped" && <span className="tert" style={{ fontSize: 10.5 }}>skipped</span>}
                  {m?.status === "failed" && <span style={{ fontSize: 10.5, color: "var(--bad)" }}>parked</span>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/* Graph canvas: stage columns as SVG; click = highlight neighborhood, click
   again = open the builder; background click clears. */
function GraphCanvas(p: {
  bundle: BundleMapV2;
  meta: WorkbenchMeta;
  onOpen: (id: string) => void;
}): JSX.Element {
  const [sel, setSel] = useState<string | null>(null);
  const stages = deriveStages(p.bundle, p.meta);
  const pos = new Map<string, { x: number; y: number }>();
  const COLW = 190;
  const ROWH = 54;
  stages.forEach((s, ci) => s.ids.forEach((id, ri) => pos.set(id, { x: ci * COLW + 20, y: ri * ROWH + 34 })));
  const H = Math.max(...stages.map((s) => s.ids.length), 1) * ROWH + 50;
  const W = stages.length * COLW + 40;
  const edges: Array<[string, string]> = [];
  for (const [id, def] of Object.entries(p.bundle))
    for (const d of agentDeps(id, def, p.bundle, p.meta)) edges.push([d, id]);
  const near = (id: string): Set<string> => {
    const n = new Set([id]);
    for (const [a, b] of edges) {
      if (a === id) n.add(b);
      if (b === id) n.add(a);
    }
    return n;
  };
  const hi = sel ? near(sel) : null;
  return (
    <div style={{ overflowX: "auto" }}>
      <svg width={W} height={H} onClick={() => setSel(null)} style={{ display: "block" }}>
        {stages.map((s, ci) => (
          <text key={s.title} x={ci * COLW + 20} y={16} fontSize={10} fill="var(--tert)" style={{ textTransform: "uppercase", letterSpacing: 1 }}>
            {ci + 1} · {s.title}
          </text>
        ))}
        {edges.map(([a, b], i) => {
          const pa = pos.get(a);
          const pb = pos.get(b);
          if (!pa || !pb) return null;
          const dim = hi && !(hi.has(a) && hi.has(b));
          return (
            <path
              key={i}
              d={`M ${pa.x + 150} ${pa.y + 14} C ${pa.x + 175} ${pa.y + 14}, ${pb.x - 25} ${pb.y + 14}, ${pb.x} ${pb.y + 14}`}
              fill="none"
              stroke={dim ? "var(--bd)" : "var(--route)"}
              strokeWidth={dim ? 1 : 1.5}
              opacity={dim ? 0.35 : 0.75}
            />
          );
        })}
        {[...pos.entries()].map(([id, xy]) => {
          const def = p.bundle[id];
          const dim = hi && !hi.has(id);
          return (
            <g
              key={id}
              transform={`translate(${xy.x},${xy.y})`}
              style={{ cursor: "pointer" }}
              opacity={dim ? 0.35 : def.enabled ? 1 : 0.5}
              onClick={(e) => {
                e.stopPropagation();
                if (sel === id) p.onOpen(id);
                else setSel(id);
              }}
            >
              <rect width={150} height={28} rx={6} fill={sel === id ? "var(--fg)" : "var(--panel)"} stroke={sel === id ? "var(--fg)" : "var(--bd2)"} />
              <text x={8} y={18} fontSize={11} fontWeight={600} fill={sel === id ? "#fff" : "var(--fg)"}>
                {def.name.length > 20 ? def.name.slice(0, 19) + "…" : def.name}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="tert" style={{ fontSize: 11, padding: "4px 0" }}>
        Click a node to highlight what feeds it and what it feeds; click again to open the builder.
      </div>
    </div>
  );
}

export function BundleView(p: BundleViewProps): JSX.Element {
  const [detail, setDetail] = useState<BundleDetailV2 | null>(null);
  const [inspecting, setInspecting] = useState<string | null>(null); // versionId
  const [builderFor, setBuilderFor] = useState<string | "new" | null>(null);
  const [canvas, setCanvas] = useState<"staged" | "graph">("staged");
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveNote, setSaveNote] = useState("");
  const [saveDiff, setSaveDiff] = useState<Array<{ agentId: string; name: string; status: string; changes: string[] }> | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [bulkModel, setBulkModel] = useState(p.settings.modelAllowlist[0] ?? "");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkErr, setBulkErr] = useState<string | null>(null);

  const reload = async (): Promise<void> => {
    const d = await getBundleV2(p.bundleId);
    if (d) setDetail(d);
  };
  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.bundleId]);

  const inspectedVersion = detail?.versions.find((v) => v.id === inspecting) ?? null;
  const map: BundleMapV2 = inspectedVersion ? inspectedVersion.snap : detail?.draft ?? {};
  const readOnly = !!inspectedVersion;
  const latest = detail?.versions[0] ?? null;
  const dirty = !!detail && (latest ? JSON.stringify(detail.draft) !== JSON.stringify(latest.snap) : Object.keys(detail.draft).length > 0);
  const stages = useMemo(() => deriveStages(map, p.meta), [map, p.meta]);
  const routerId = Object.keys(map).find((id) => map[id].role === "router" && map[id].enabled) ?? null;
  const cleanerId = Object.keys(map).find((id) => map[id].role === "cleaner" && map[id].enabled) ?? null;
  const customCount = Object.keys(map).filter((id) => !p.meta.seedAgents[id]).length;

  useEffect(() => {
    if (!detail) return;
    const selected = [...new Set(Object.values(detail.draft).map((def) => def.model))];
    if (selected.length === 1) setBulkModel(selected[0]);
  }, [detail]);

  /* Draft diff summary for the rail's Draft row. */
  const draftDiffSummary = useMemo(() => {
    if (!detail || !latest) return dirty ? "unsaved agents" : "";
    let added = 0, removed = 0, changed = 0;
    const ids = new Set([...Object.keys(detail.draft), ...Object.keys(latest.snap)]);
    for (const id of ids) {
      const a = latest.snap[id];
      const b = detail.draft[id];
      if (a && !b) removed++;
      else if (!a && b) added++;
      else if (JSON.stringify(a) !== JSON.stringify(b)) changed++;
    }
    if (!added && !removed && !changed) return "";
    return [added && `+${added} agent${added > 1 ? "s" : ""}`, removed && `−${removed}`, changed && `${changed} changed`].filter(Boolean).join(" · ");
  }, [detail, latest, dirty]);

  /* ---- run one email ---- */
  const runScope = `test:${p.bundleId}`;
  const [runCorpusId, setRunCorpusId] = useState<string>(p.corpuses[0]?.id ?? "");
  const runCorpus = p.corpuses.find((c) => c.id === runCorpusId) ?? null;
  const syncedEmails = (runCorpus?.emails ?? []).filter((e) => e.serverId);
  const [runEmailId, setRunEmailId] = useState<number | null>(null);
  useEffect(() => {
    if (runEmailId == null || !syncedEmails.some((e) => e.id === runEmailId))
      setRunEmailId(syncedEmails[0]?.id ?? null);
  }, [runCorpusId, syncedEmails.length, runEmailId, syncedEmails]);
  const [running, setRunning] = useState<{ done: number } | null>(null);
  const [runErr, setRunErr] = useState<string | null>(null);
  const [panel, setPanel] = useState<RunPanelData | null>(null);
  const [panelCtx, setPanelCtx] = useState<{ subject: string; localId: number; against: string } | null>(null);
  const [runMarks, setRunMarks] = useState<Record<string, RunMark> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  /* Annotations don't survive switching what the canvas shows. */
  useEffect(() => {
    setRunMarks(null);
  }, [inspecting, p.bundleId]);

  function panelFromResult(res: ExecResult): RunPanelData {
    const perAgent: RunPanelData["usage"]["perAgent"] = {};
    for (const c of res.checks) {
      if (c.skipped) continue;
      const cur = perAgent[c.id] || { cost: 0, calls: 0, model: c.model };
      perAgent[c.id] = { cost: cur.cost + c.usage.cost, calls: cur.calls + 1, model: c.model || cur.model };
    }
    const st = res.updatedState;
    return {
      routes: res.routes,
      unrouted: !res.routes.length,
      tray: res.tray,
      memos: (st.memos ?? {}) as RunPanelData["memos"],
      register: (st.documentRegister ?? {}) as RunPanelData["register"],
      trackers: (st.trackers ?? {}) as RunPanelData["trackers"],
      checks: res.checks,
      usage: { cost: res.usage.cost, perAgent },
      saDispatches: res.saDispatches,
      warnings: res.warnings,
      degraded: res.degraded,
    };
  }

  function marksFromResult(res: ExecResult): Record<string, RunMark> {
    let marks: Record<string, RunMark> = {};
    for (const c of res.checks) {
      if (c.id.startsWith("_")) continue;
      marks = mergeMark(marks, c.id, checkToMark(c));
    }
    return marks;
  }

  async function runEmail(overrideEmailId?: number): Promise<void> {
    const email = syncedEmails.find((e) => e.id === (overrideEmailId ?? runEmailId));
    if (!email?.serverId) return;
    setRunning({ done: 0 });
    setRunErr(null);
    setPanel(null);
    setRunMarks({});
    /* Set the drawer's context up front so it opens with the run, not after. */
    setPanelCtx({
      subject: email.subject,
      localId: email.id,
      against: readOnly && inspectedVersion ? inspectedVersion.label : "draft",
    });
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      await clearTrayScope(runScope);
      const res = await streamExecute(
        email.serverId,
        {
          ...(readOnly && inspectedVersion ? { bundleVersionId: inspectedVersion.id } : { bundleId: p.bundleId }),
          workstreams: p.workstreams.map((w) => ({ id: w.id, name: w.name, code: w.code, entities: w.entities })),
          state: { memos: {}, documentRegister: {}, participantMap: {}, threadHistory: [], trackers: {} },
          localId: email.id,
          runScope,
        },
        (check) => {
          setRunning((r) => (r ? { done: r.done + 1 } : r));
          if (!check.id.startsWith("_")) setRunMarks((prev) => mergeMark(prev ?? {}, check.id, checkToMark(check)));
        },
        ctrl.signal,
        (start) => {
          if (!start.id.startsWith("_")) setRunMarks((prev) => mergeMark(prev ?? {}, start.id, { status: "running" }));
        },
      );
      setPanel(panelFromResult(res));
      setRunMarks(marksFromResult(res));
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") setPanelCtx(null);
      else setRunErr(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(null);
      abortRef.current = null;
    }
  }

  /* ---- tray actions from the panel ---- */
  async function onResolve(item: TrayItem, action: "approve" | "reject" | "choose" | "answer" | "acknowledge", extra?: { choice?: string; text?: string }): Promise<void> {
    const r = await resolveTrayItem(item.id, action, extra ?? {});
    if (!r) return;
    setPanel((pd) =>
      pd
        ? {
            ...pd,
            tray: pd.tray.map((t) => (t.id === item.id ? r.item : t)),
            memos: r.writeBack
              ? Object.fromEntries(
                  Object.entries(pd.memos).map(([ws, m]) => [
                    ws,
                    item.matters.includes(ws) || item.wsId === ws
                      ? {
                          ...m,
                          fields: {
                            ...m.fields,
                            [r.writeBack!.field]: { value: r.writeBack!.value, source: "Resolved by lawyer", stale: false },
                          },
                        }
                      : m,
                  ]),
                )
              : pd.memos,
          }
        : pd,
    );
  }
  async function onRuling(item: TrayItem, text: string): Promise<void> {
    await recordRuling(item.id, text);
  }

  /* ---- builder save/remove ---- */
  async function saveAgent(id: string, def: AgentDefV2): Promise<void> {
    if (!detail) return;
    const draft: BundleMapV2 = { ...detail.draft, [id]: def };
    await replaceDraftV2(p.bundleId, draft);
    setBuilderFor(null);
    await reload();
  }
  async function applyModelToAllAgents(): Promise<void> {
    if (!detail || readOnly || !bulkModel) return;
    const count = Object.keys(detail.draft).length;
    const label = p.models.find((model) => model.id === bulkModel)?.name ?? bulkModel;
    if (!window.confirm(`Set all ${count} agents to ${label}? Each agent's ${label} profile will activate automatically when available.`)) return;
    setBulkSaving(true);
    setBulkErr(null);
    try {
      const ok = await replaceDraftV2(p.bundleId, setBundleModel(detail.draft, bulkModel));
      if (!ok) throw new Error("The draft could not be updated.");
      await reload();
    } catch (error) {
      setBulkErr(error instanceof Error ? error.message : String(error));
    } finally {
      setBulkSaving(false);
    }
  }
  async function removeAgent(id: string): Promise<void> {
    if (!detail) return;
    if (!window.confirm(`Remove "${detail.draft[id]?.name ?? id}" from this bundle's draft?`)) return;
    await apiDeleteAgent(p.bundleId, id);
    setBuilderFor(null);
    await reload();
  }
  async function testRunFromBuilder(id: string, def: AgentDefV2, emailServerId: string, onProgress?: () => void): Promise<ExecResult> {
    if (!detail) throw new Error("bundle not loaded");
    await replaceDraftV2(p.bundleId, { ...detail.draft, [id]: def });
    await clearTrayScope(runScope);
    const res = await streamExecute(
      emailServerId,
      {
        bundleId: p.bundleId,
        workstreams: p.workstreams.map((w) => ({ id: w.id, name: w.name, code: w.code, entities: w.entities })),
        state: { memos: {}, documentRegister: {}, participantMap: {}, threadHistory: [], trackers: {} },
        runScope,
        trace: true,
      },
      () => onProgress?.(),
    );
    await reload();
    return res;
  }

  const wsName = (id: string): string => p.workstreams.find((w) => w.id === id)?.name ?? (id === "unassoc" ? "Unassociated" : id);

  if (!detail)
    return (
      <div className="tert" style={{ padding: 24 }}>
        <Loader2 size={14} className="spinrot" /> Loading bundle…
      </div>
    );

  return (
    <div className="wbfull">
      {/* Header band ① */}
      <div className="bhead">
        <button className="back" onClick={p.onBack}>
          <ChevronLeft size={13} /> Agent Registry
        </button>
        <input
          className="bname-in"
          aria-label="Bundle name"
          value={detail.name}
          onChange={(e) => {
            setDetail({ ...detail, name: e.target.value });
            void updateBundle(p.bundleId, { name: e.target.value });
          }}
          style={{ flex: "0 1 320px" }}
        />
        {dirty && !readOnly && <span className="chip dirty">draft · unsaved changes</span>}
        <span className="spacer" />
        {detail.name === "Rahaf's copy - Standard triage bundle" && Object.keys(detail.draft).length > 15 && !readOnly && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={async () => {
              if (!window.confirm("Apply the agreed six merges and deterministic State of Play to Rahaf's draft only?")) return;
              const result = await consolidateRahafDraft(p.bundleId);
              if (result) await reload();
            }}
          >
            Consolidate to 15 agents
          </button>
        )}
        <button
          className="btn btn-ghost btn-sm"
          title="Copy the current draft into a new bundle (empty version history)"
          onClick={async () => {
            const name = window.prompt("Save As… — name for the new bundle:", `${detail.name} (copy)`);
            if (!name) return;
            await duplicateBundle(p.bundleId, name);
            p.onBack();
          }}
        >
          <Copy size={13} /> Save As…
        </button>
        <button
          className="btn btn-ghost btn-sm"
          disabled={readOnly || !dirty}
          onClick={async () => {
            setSaveDiff(null);
            setSaveOpen(true);
            if (latest) {
              const d = await diffDraft(p.bundleId, latest.id);
              if (d) setSaveDiff(d.items.filter((i) => i.status !== "unchanged"));
            } else setSaveDiff([]);
          }}
        >
          Save version
        </button>
        <button className="btn btn-ghost btn-sm" title="Delete this bundle" style={{ color: "var(--bad)" }} onClick={() => setConfirmDelete(true)}>
          <Trash2 size={13} /> Delete
        </button>
        <button className="btn btn-primary btn-sm" disabled={readOnly} onClick={() => setBuilderFor("new")}>
          <Plus size={13} /> Add agent
        </button>
      </div>
      {/* Meta line */}
      <div className="bmeta">
        {Object.keys(map).length} agents · router:{" "}
        {routerId ? map[routerId].name : <span style={{ color: "var(--bad)" }}>missing — emails will file as unassociated</span>} · cleaner:{" "}
        {cleanerId ? map[cleanerId].name : <span style={{ color: "var(--sem)" }}>none (raw body)</span>}
        {customCount > 0 && <> · {customCount} custom</>}
      </div>
      {readOnly && inspectedVersion && (
        <div className="robar" role="status" style={{ borderTop: "1px solid var(--bd)" }}>
          Viewing <b>{inspectedVersion.label}</b> (read-only — saved {fmtDate(inspectedVersion.createdAt)}). Agents open in inspect mode.
          <span className="spacer" />
          <button
            className="btn btn-sm"
            style={{ background: "var(--sem)", color: "var(--panel)", border: "none" }}
            onClick={async () => {
              if (!window.confirm(`Replace the draft with ${inspectedVersion.label}? Unsaved draft changes are lost.`)) return;
              await revertDraft(p.bundleId, inspectedVersion.id);
              setInspecting(null);
              await reload();
            }}
          >
            Restore this version to draft
          </button>
          <button className="btn btn-sm" style={{ color: "var(--sem)", border: "1px solid var(--sem)", background: "transparent" }} onClick={() => setInspecting(null)}>
            Back to draft
          </button>
        </div>
      )}

      <div className="bwrap">
        {/* Version rail */}
        <aside className="vrail">
          <h3>Version history</h3>
          <button className={"ventry" + (!inspecting ? " on" : "")} onClick={() => setInspecting(null)}>
            <span className="vl">
              Draft {dirty && <span className="chip dirty">unsaved</span>}
            </span>
            <span className="vd" style={{ display: "block" }}>live working set · {Object.keys(detail.draft).length} agents</span>
            {dirty && draftDiffSummary && <span className="vdiff" style={{ display: "block" }}>{draftDiffSummary}</span>}
            {!dirty && <span className="vd" style={{ display: "block" }}>matches latest version</span>}
          </button>
          <div className="divider" />
          {detail.versions.map((v, i) => {
            const prev = detail.versions[i + 1];
            let diffTxt = "";
            if (prev) {
              let added = 0, removed = 0, changed = 0;
              const ids = new Set([...Object.keys(v.snap), ...Object.keys(prev.snap)]);
              for (const id of ids) {
                const a = prev.snap[id];
                const b = v.snap[id];
                if (a && !b) removed++;
                else if (!a && b) added++;
                else if (JSON.stringify(a) !== JSON.stringify(b)) changed++;
              }
              diffTxt = [added && `+${added} agent${added > 1 ? "s" : ""}`, removed && `−${removed}`, changed && `${changed} changed`].filter(Boolean).join(" · ") || "no changes";
            } else diffTxt = "first version";
            const hasDiff = diffTxt !== "no changes" && diffTxt !== "first version";
            return (
              <button key={v.id} className={"ventry" + (inspecting === v.id ? " on" : "")} onClick={() => setInspecting(v.id)}>
                <span className="vl">{v.label}</span>
                <span className="vd" style={{ display: "block" }}>
                  {fmtDate(v.createdAt)} · {Object.keys(v.snap).length} agents
                </span>
                <span className={hasDiff ? "vdiff" : "vd"} style={{ display: "block" }}>{diffTxt}</span>
                {v.note && <span className="vd" style={{ display: "block", fontStyle: "italic" }}>{v.note}</span>}
              </button>
            );
          })}
          {detail.versions.length === 0 && (
            <div className="tert" style={{ fontSize: 11.5, lineHeight: 1.5, padding: "4px 14px" }}>
              No versions yet — a simulation can only run a saved version.
            </div>
          )}
        </aside>

        {/* Main */}
        <div className="bmain">
          {/* Toolbar — one line: view toggle · test-with selects · Run email */}
          <div className="toolrow">
            <div className="viewtog">
              <button title="Staged view" aria-label="Staged view" className={canvas === "staged" ? "on" : ""} onClick={() => setCanvas("staged")}>
                <Rows3 size={14} />
              </button>
              <button title="Dependency graph" aria-label="Dependency graph" className={canvas === "graph" ? "on" : ""} onClick={() => setCanvas("graph")}>
                <GitBranch size={14} />
              </button>
            </div>
            <span className="tlabel">Test with</span>
            <select className="sel" value={runCorpusId} onChange={(e) => setRunCorpusId(e.target.value)} style={{ fontSize: 12, padding: "5px 26px 5px 8px" }}>
              {p.corpuses.map((c) => (
                <option key={c.id} value={c.id}>{c.name} · {c.emails.length} email{c.emails.length === 1 ? "" : "s"}</option>
              ))}
            </select>
            <select className="sel" value={runEmailId ?? ""} onChange={(e) => setRunEmailId(Number(e.target.value))} style={{ fontSize: 12, maxWidth: 300, padding: "5px 26px 5px 8px" }}>
              {syncedEmails.map((e) => (
                <option key={e.id} value={e.id}>
                  #{e.id} — {e.subject.slice(0, 40)}{(e.attachments?.length ?? 0) > 0 ? ` · ${e.attachments!.length} doc${e.attachments!.length === 1 ? "" : "s"}` : ""}
                </option>
              ))}
            </select>
            {running ? (
              <button className="btn btn-sm" onClick={() => abortRef.current?.abort()}>
                <Loader2 size={13} className="spinrot" /> {running.done} agents · Stop
              </button>
            ) : (
              <button className="btn btn-primary btn-sm" disabled={!syncedEmails.length} onClick={() => void runEmail()}>
                <Play size={13} /> Run email ▸
              </button>
            )}
            {runMarks && !running && (
              <span className="tert" style={{ fontSize: 11.5 }}>
                canvas shows the last run — <button className="lnk" style={{ fontSize: 11.5 }} onClick={() => setRunMarks(null)}>clear</button>
              </span>
            )}
            <span className="tlabel" style={{ marginLeft: "auto" }}>All agents</span>
            <select className="sel" disabled={readOnly || bulkSaving} value={bulkModel} onChange={(e) => setBulkModel(e.target.value)} style={{ fontSize: 12, maxWidth: 210, padding: "5px 26px 5px 8px" }}>
              {p.settings.modelAllowlist.map((model) => (
                <option key={model} value={model}>{p.models.find((candidate) => candidate.id === model)?.name ?? model}</option>
              ))}
            </select>
            <button className="btn btn-sm" disabled={readOnly || bulkSaving || !bulkModel} onClick={() => void applyModelToAllAgents()}>
              {bulkSaving ? <Loader2 size={13} className="spinrot" /> : null}
              Set all
            </button>
          </div>
          {bulkErr && (
            <div className="robar" role="alert" style={{ background: "var(--bad-soft)", color: "var(--bad)" }}>
              Model update failed — {bulkErr} <button className="lnk" onClick={() => setBulkErr(null)}>Dismiss</button>
            </div>
          )}
          {runErr && (
            <div className="robar" role="alert" style={{ background: "var(--bad-soft)", color: "var(--bad)" }}>
              Run failed — {runErr} <button className="lnk" onClick={() => setRunErr(null)}>Dismiss</button>
            </div>
          )}

          <div className="canvas-wrap">
            {canvas === "staged" ? (
              <div className="board">
                {stages.map((s, si) => (
                  <div key={s.title} className="stagecol">
                    <div className="stage-h">
                      <span>{si + 1} · {s.title}</span>
                      <span className="mono">{s.ids.length}</span>
                    </div>
                    {s.ids.map((id) => (
                      <AgentCard
                        key={id}
                        id={id}
                        def={map[id]}
                        deps={agentDeps(id, map[id], map, p.meta)}
                        bundle={map}
                        meta={p.meta}
                        seed={!!p.meta.seedAgents[id]}
                        mark={runMarks?.[id] ?? null}
                        onOpen={() => setBuilderFor(id)}
                      />
                    ))}
                  </div>
                ))}
                {Object.keys(map).length === 0 && (
                  <div className="tert" style={{ fontSize: 12.5, padding: 20 }}>
                    Empty bundle — add agents to define the pipeline.
                  </div>
                )}
              </div>
            ) : (
              <GraphCanvas bundle={map} meta={p.meta} onOpen={(id) => setBuilderFor(id)} />
            )}
          </div>
        </div>
      </div>

      {/* Run drawer — opens with the run: live agent progress first, then the
          full result panel (tray, memo, checks, usage) once complete. */}
      {(panel || (running && panelCtx)) && (
        <>
          <div
            className="backdrop"
            style={{ zIndex: 55, background: "rgba(15,18,22,.38)" }}
            onClick={() => { if (!running) { setPanel(null); setPanelCtx(null); } }}
          />
          <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "min(600px, 95vw)", zIndex: 56, background: "var(--panel)", borderLeft: "1px solid var(--bd)", display: "flex", flexDirection: "column", boxShadow: "0 14px 40px rgba(20,24,30,.14)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--bd)", flexShrink: 0 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {panelCtx ? `#${panelCtx.localId} — ${panelCtx.subject}` : "Run result"}
                </div>
                <div className="tert" style={{ fontSize: 11.5 }}>
                  {runCorpus?.name ?? "corpus"} · run against <strong style={{ color: "var(--fg)" }}>{detail.name} — {panelCtx?.against ?? "draft"}</strong>
                </div>
              </div>
              <span className="spacer" />
              {running ? (
                <button className="btn btn-sm" onClick={() => abortRef.current?.abort()}>Stop</button>
              ) : (
                <button className="icon-btn" onClick={() => { setPanel(null); setPanelCtx(null); }} title="Close result">
                  <X size={15} />
                </button>
              )}
            </div>
            <div style={{ flex: 1, minHeight: 0, padding: "12px 16px" }}>
              {panel ? (
                <RunResultPanel
                  data={panel}
                  wsName={wsName}
                  tauFor={(id) => map[id]?.tau ?? null}
                  agentName={(id) => map[id]?.name ?? (id === "_semantic" ? "Semantic triggers" : id)}
                  onResolve={(item, action, extra) => void onResolve(item, action, extra)}
                  onRuling={(item, text) => void onRuling(item, text)}
                  onRecompute={() => void runEmail()}
                />
              ) : (
                <RunProgress stages={stages} map={map} marks={runMarks ?? {}} done={running?.done ?? 0} />
              )}
            </div>
          </div>
        </>
      )}

      {/* Builder drawer */}
      {builderFor && (
        <>
          <div className="backdrop" style={{ zIndex: 60, background: "rgba(15,18,22,.38)" }} onClick={() => setBuilderFor(null)} />
          <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "min(720px, 96vw)", zIndex: 61, background: "var(--panel)", borderLeft: "1px solid var(--bd)", display: "flex", flexDirection: "column", padding: 12, overflowY: "auto", boxShadow: "0 14px 40px rgba(20,24,30,.14)" }}>
            <AgentBuilder
              key={builderFor}
              agentId={builderFor === "new" ? null : builderFor}
              initial={builderFor === "new" ? freshAgent(p.settings.modelAllowlist[0] ?? "anthropic/claude-sonnet-4.6") : map[builderFor]}
              bundle={map}
              meta={p.meta}
              models={p.models}
              allowlist={p.settings.modelAllowlist}
              evaluatorModel={p.settings.semanticEvaluatorModel}
              readOnly={readOnly}
              corpuses={p.corpuses}
              onSave={(id, def) => void saveAgent(id, def)}
              onRemove={(id) => void removeAgent(id)}
              onClose={() => setBuilderFor(null)}
              onTestRun={testRunFromBuilder}
            />
          </div>
        </>
      )}

      {/* Save-version modal */}
      {saveOpen && (
        <>
          <div className="backdrop" style={{ zIndex: 60, background: "rgba(15,18,22,.45)" }} onClick={() => setSaveOpen(false)} />
          <div className="card" style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 61, width: "min(480px, 92vw)", padding: 18 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Save version</div>
            <div className="tert" style={{ fontSize: 12, marginBottom: 10 }}>
              Snapshots the draft immutably; the label is assigned automatically.
            </div>
            <div className="up" style={{ marginBottom: 4 }}>Changes vs {latest?.label ?? "—"}</div>
            <div style={{ maxHeight: 180, overflowY: "auto", marginBottom: 10 }}>
              {saveDiff === null && <span className="tert" style={{ fontSize: 12 }}>computing…</span>}
              {saveDiff !== null && saveDiff.length === 0 && <span className="tert" style={{ fontSize: 12 }}>{latest ? "No changes." : "First version of this bundle."}</span>}
              {(saveDiff ?? []).map((i) => (
                <div key={i.agentId} style={{ fontSize: 12, padding: "2px 0" }}>
                  <span className="mono" style={{ color: i.status === "added" ? "var(--acc)" : i.status === "removed" ? "var(--bad)" : "var(--sem)" }}>
                    {i.status === "added" ? "+" : i.status === "removed" ? "−" : "~"}
                  </span>{" "}
                  <strong>{i.name}</strong> {i.changes.length > 0 && <span className="tert">— {i.changes.join(", ")}</span>}
                </div>
              ))}
            </div>
            <textarea className="inp tarea" placeholder="Optional note — why this version exists" style={{ minHeight: 56, width: "100%" }} value={saveNote} onChange={(e) => setSaveNote(e.target.value)} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
              <button className="btn btn-sm" onClick={() => setSaveOpen(false)}>Cancel</button>
              <button
                className="btn btn-primary btn-sm"
                onClick={async () => {
                  await saveVersion(p.bundleId, saveNote.trim() || undefined);
                  setSaveOpen(false);
                  setSaveNote("");
                  await reload();
                }}
              >
                Save version
              </button>
            </div>
          </div>
        </>
      )}

      {/* Delete confirm */}
      {confirmDelete && (
        <>
          <div className="backdrop" style={{ zIndex: 60, background: "rgba(15,18,22,.45)" }} onClick={() => setConfirmDelete(false)} />
          <div className="card" style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 61, width: "min(440px, 92vw)", padding: 18 }}>
            <div style={{ fontWeight: 700, marginBottom: 8, color: "var(--bad)" }}>Delete "{detail.name}"?</div>
            <div style={{ fontSize: 12.5, lineHeight: 1.6, marginBottom: 12 }}>
              This deletes the draft and all {detail.versions.length} saved version{detail.versions.length === 1 ? "" : "s"}. Simulations pinned to this bundle's versions will no longer run. This cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-sm" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button
                className="btn btn-sm"
                style={{ color: "var(--bad)", borderColor: "var(--bad)" }}
                onClick={async () => {
                  const { safeFetch } = await import("../api/client");
                  const r = await safeFetch(`/agent-bundles/${encodeURIComponent(p.bundleId)}`, { method: "DELETE" });
                  setConfirmDelete(false);
                  if (r != null) p.onDeleted();
                }}
              >
                Delete bundle
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
